<template>
  <div>
    <b-card>
      <b-overlay
        :show="loading"
        rounded="sm"
      >
        <b-row>
          <b-col
            v-for="corretora in corretoras"
            :key="corretora.id"
            lg="6"
            xl="6"
            class="mb-3"
          >
            <b-card
              no-body
              class="h-100"
              :class="cardBorderClass(corretora.id)"
            >
              <b-card-body>
                <!-- Header -->
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <div class="d-flex align-items-center">
                    <div class="avatar avatar-sm bg-light-primary rounded mr-1">
                      <img
                        v-if="corretora.nome.toLowerCase() === 'binance'"
                        src="@/assets/images/logo/binance-logo.png"
                        alt="Binance"
                        class="img-fluid"
                        style="width: 20px; height: 20px; object-fit: contain;"
                      >
                      <img
                        v-else-if="corretora.nome.toLowerCase() === 'mexc'"
                        src="@/assets/images/logo/mexc-logo.png"
                        alt="MEXC"
                        class="img-fluid"
                        style="width: 20px; height: 20px; object-fit: contain;"
                      >
                      <feather-icon
                        v-else
                        icon="CreditCardIcon"
                        size="14"
                      />
                    </div>
                    <div>
                      <h6 class="mb-0 font-weight-bold">
                        {{ corretora.nome }}
                      </h6>
                      <small class="text-muted">{{ $t('contaConfiguracao.exchangeApi.exchange') }}</small>
                    </div>
                  </div>
                </div>

                <!-- Informações da API -->
                <div
                  v-if="getStatus(corretora.id).active"
                  class="mt-2"
                >
                  <div class="mb-2">
                    <small class="text-muted d-block mb-1">{{ $t('contaConfiguracao.exchangeApi.apiKey') }}</small>
                    <code class="bg-light px-2 py-1 rounded d-block text-truncate">
                      {{ maskKey(getStatus(corretora.id).apiKey) }}
                    </code>
                  </div>
                </div>

                <!-- Estado não configurado -->
                <div
                  v-else
                  class="text-center py-3"
                >
                  <feather-icon
                    icon="KeyIcon"
                    size="32"
                    class="text-muted mb-2"
                  />
                  <h6 class="text-muted mb-2">
                    {{ $t('contaConfiguracao.exchangeApi.naoConfigurada') }}
                  </h6>
                  <p class="text-muted small mb-0">
                    {{ $t('contaConfiguracao.exchangeApi.naoConfiguradaDesc') }}
                  </p>
                </div>

                <!-- Botões de Ação -->
                <div class="mt-3 pt-2 border-top">
                  <b-button
                    v-if="!getStatus(corretora.id).active"
                    v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                    variant="primary"
                    class="mr-1 mt-1"
                    @click="openForm(corretora.id, 'create')"
                  >
                    <feather-icon
                      icon="PlusIcon"
                      size="18"
                      class="mr-50"
                    />
                    {{ $t('contaConfiguracao.exchangeApi.adicionarApi') }}
                  </b-button>

                  <template v-else>
                    <b-button
                      v-ripple.400="'rgba(59, 130, 246, 0.15)'"
                      variant="primary"
                      class="mr-1 mt-1"
                      @click="showApiDetails(corretora.id)"
                    >
                      {{ $t('contaConfiguracao.exchangeApi.visualizar') }}
                    </b-button>
                  </template>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </b-overlay>
    </b-card>

    <!-- Formulário de Cadastro/Edição -->
    <b-modal
      v-model="showForm"
      :title="modalTitle()"
      hide-footer
      size="lg"
      centered
    >
      <b-overlay
        :show="loadingForm"
        rounded="sm"
      >
        <validation-observer ref="simpleRules">
          <b-form
            autocomplete="off"
            @submit.prevent="validationForm"
          >
            <b-row>
              <!-- API Key -->
              <b-col cols="12">
                <b-form-group
                  :label="$t('contaConfiguracao.exchangeApi.labelApiKey')"
                  label-for="exchange-api-key"
                >
                  <validation-provider
                    #default="{ errors }"
                    :name="$t('contaConfiguracao.exchangeApi.labels.apiKey')"
                    rules="required"
                  >
                    <b-input-group class="input-group-merge">
                      <b-input-group
                        class="input-group-merge"
                        :class="errors.length > 0 ? 'is-invalid':null"
                      >
                        <b-form-input
                          id="exchange-api-key"
                          v-model="formData.apiKey"
                          type="text"
                          :state="errors.length > 0 ? false:null"
                          :placeholder="$t('contaConfiguracao.exchangeApi.placeholders.apiKey')"
                          autocomplete="off"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            icon="KeyIcon"
                            size="14"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-input-group>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- API Secret -->
              <b-col cols="12">
                <b-form-group
                  :label="$t('contaConfiguracao.exchangeApi.labelApiSecret')"
                  label-for="exchange-api-secret"
                >
                  <!-- Modo Edição: Mostrar asteriscos apenas -->
                  <div v-if="formMode === 'edit'">
                    <b-input-group class="input-group-merge">
                      <b-input-group class="input-group-merge">
                        <b-form-input
                          id="exchange-api-secret"
                          value="****************************************"
                          type="password"
                          readonly
                          disabled
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            icon="LockIcon"
                            size="14"
                            class="text-muted"
                          />
                        </b-input-group-append>
                      </b-input-group>
                    </b-input-group>
                    <small class="text-muted">{{ $t('contaConfiguracao.exchangeApi.messages.secretKeyHidden') }}</small>
                  </div>

                  <!-- Modo Criação: Campo editável -->
                  <validation-provider
                    v-else
                    #default="{ errors }"
                    :name="$t('contaConfiguracao.exchangeApi.labels.apiSecret')"
                    rules="required"
                  >
                    <b-input-group class="input-group-merge">
                      <b-input-group
                        class="input-group-merge"
                        :class="errors.length > 0 ? 'is-invalid':null"
                      >
                        <b-form-input
                          id="exchange-api-secret"
                          v-model="formData.apiSecret"
                          :type="showSecretKey ? 'text' : 'password'"
                          :state="errors.length > 0 ? false:null"
                          :placeholder="$t('contaConfiguracao.exchangeApi.placeholders.apiSecret')"
                          autocomplete="off"
                        />
                        <b-input-group-append is-text>
                          <feather-icon
                            :icon="showSecretKey ? 'EyeOffIcon' : 'EyeIcon'"
                            size="14"
                            class="cursor-pointer"
                            @click="toggleSecretKeyVisibility"
                          />
                        </b-input-group-append>
                      </b-input-group>
                      <small class="text-danger">{{ errors[0] }}</small>
                    </b-input-group>
                  </validation-provider>
                </b-form-group>
              </b-col>

              <!-- Buttons -->
              <b-col cols="12">
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  type="submit"
                  class="mr-1 mt-1"
                >
                  {{ $t('contaConfiguracao.salvarBtn') }}
                </b-button>
                <b-button
                  v-ripple.400="'rgba(186, 191, 199, 0.15)'"
                  variant="outline-secondary"
                  class="mt-1"
                  @click="showForm = false"
                >
                  {{ $t('cancelar') }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-overlay>
    </b-modal>

    <!-- Modal de Detalhes da API -->
    <b-modal
      v-model="showDetailsModal"
      :title="$t('contaConfiguracao.exchangeApi.detalhesApi')"
      hide-footer
      size="lg"
      centered
      @hidden="closeDetailsModal"
    >
      <b-overlay
        :show="loadingApiDetails"
        rounded="sm"
      >
        <div
          v-if="errorLoadingDetails"
          class="text-center p-4"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="32"
            class="text-danger mb-2"
          />
          <p class="mb-0">
            {{ errorLoadingDetails }}
          </p>
          <b-button
            v-ripple.400="'rgba(59, 130, 246, 0.15)'"
            variant="outline-primary"
            class="mt-1"
            @click="showApiDetails(selectedApiDetails.corretoraId)"
          >
            {{ $t('comum.tentarNovamente') }}
          </b-button>
        </div>

        <!-- Cabeçalho dos Detalhes -->
        <!-- Alerta de API Inválida -->
        <b-alert
          v-if="selectedApiDetails && selectedApiDetails.apiInvalid"
          variant="danger"
          show
          class="mb-2"
        >
          <div class="d-flex align-items-start">
            <feather-icon
              icon="AlertTriangleIcon"
              size="16"
              class="mr-2 mt-25"
            />
            <div>
              <div class="font-weight-bold mb-25">
                {{ $t('contaConfiguracao.exchangeApi.apiInvalidaTitulo') }}
              </div>
              <div class="text-danger">
                {{ selectedApiDetails.errorMessage || $t('contaConfiguracao.exchangeApi.erroValidarApi') || 'Erro ao validar a API' }}
              </div>
            </div>
          </div>
        </b-alert>

        <!-- Ações quando API está inválida -->
        <div
          v-if="selectedApiDetails && selectedApiDetails.apiInvalid"
          class="pt-2 border-top mb-2"
        >
          <b-button
            v-ripple.400="'rgba(234, 84, 85, 0.15)'"
            variant="outline-danger"
            class="mr-1 mt-1"
            @click="removeAPI(selectedCorretoraId)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="14"
              class="mr-50"
            />
            {{ $t('contaConfiguracao.exchangeApi.excluir') }}
          </b-button>

          <b-button
            v-ripple.400="'rgba(186, 191, 199, 0.15)'"
            variant="outline-secondary"
            class="mt-1"
            @click="closeDetailsModal"
          >
            {{ $t('contaConfiguracao.exchangeApi.fechar') }}
          </b-button>
        </div>

        <!-- Informações da API -->
        <div
          v-if="selectedApiDetails && !selectedApiDetails.apiInvalid"
          class="mb-3"
        >
          <!-- Header -->
          <div class="d-flex align-items-center mb-3 pb-2 border-bottom">
            <img
              v-if="corretoraNome(selectedCorretoraId).toLowerCase() === 'binance'"
              src="@/assets/images/logo/binance-logo.png"
              alt="Binance"
              class="mr-1"
              style="width: 24px; height: 24px; object-fit: contain;"
            >
            <img
              v-else-if="corretoraNome(selectedCorretoraId).toLowerCase() === 'mexc'"
              src="@/assets/images/logo/mexc-logo.png"
              alt="MEXC"
              class="mr-1"
              style="width: 24px; height: 24px; object-fit: contain;"
            >
            <feather-icon
              v-else
              icon="CreditCardIcon"
              size="24"
              class="mr-1 text-muted"
            />
            <div>
              <h6 class="mb-0">
                {{ corretoraNome(selectedCorretoraId) }}
              </h6>
              <!--small class="text-muted">Criação: {{ formatDateBR(selectedApiDetails.createdAt) }}</small-->
            </div>
          </div>

          <!-- Status Trading -->
          <div class="mb-3">
            <b-row>
              <b-col md="6">
                <div class="d-flex align-items-center mb-2">
                  <span class="mr-1">{{ $t('contaConfiguracao.exchangeApi.tradingSpot') }}:</span>
                  <b-badge
                    :variant="selectedApiDetails.spotEnabled ? 'success' : 'secondary'"
                  >
                    {{ selectedApiDetails.spotEnabled ? $t('contaConfiguracao.exchangeApi.habilitado') : $t('contaConfiguracao.exchangeApi.desabilitado') }}
                  </b-badge>
                </div>
              </b-col>
              <b-col md="6">
                <div class="d-flex align-items-center mb-2">
                  <span class="mr-1">{{ $t('contaConfiguracao.exchangeApi.tradingFutures') }}:</span>
                  <b-badge
                    :variant="selectedApiDetails.futuresEnabled ? 'success' : 'secondary'"
                  >
                    {{ selectedApiDetails.futuresEnabled ? $t('contaConfiguracao.exchangeApi.habilitado') : $t('contaConfiguracao.exchangeApi.desabilitado') }}
                  </b-badge>
                </div>
              </b-col>
            </b-row>
            <div
              v-if="selectedApiDetails.positionMode"
              class="d-flex align-items-center"
            >
              <span class="mr-1">{{ $t('contaConfiguracao.exchangeApi.modoPosicao') }}:</span>
              <b-badge
                :variant="selectedApiDetails.positionMode === 'hedge' ? 'warning' : 'info'"
              >
                {{ selectedApiDetails.positionMode === 'hedge' ? $t('contaConfiguracao.exchangeApi.hedge') : $t('contaConfiguracao.exchangeApi.oneWay') }}
              </b-badge>
              <b-button
                v-if="selectedApiDetails.futuresEnabled && selectedCorretoraId === 1"
                v-ripple.400="'rgba(59, 130, 246, 0.15)'"
                variant="outline-primary"
                size="sm"
                class="ml-2"
                :disabled="loadingPositionMode"
                @click="togglePositionMode"
              >
                <b-spinner
                  v-if="loadingPositionMode"
                  small
                  class="mr-50"
                />
                <feather-icon
                  v-else
                  :icon="selectedApiDetails.positionMode === 'hedge' ? 'ToggleLeftIcon' : 'ToggleRightIcon'"
                  size="14"
                  class="mr-50"
                />
                {{ $t('contaConfiguracao.exchangeApi.alternarModo') }}
              </b-button>
            </div>
          </div>

          <!-- API Key -->
          <b-form-group
            :label="$t('contaConfiguracao.exchangeApi.labelApiKey')"
            label-for="api-key-display"
          >
            <b-form-input
              id="api-key-display"
              :value="selectedApiDetails.apiKey"
              readonly
              class="font-monospace"
            />
          </b-form-group>

          <!-- Informações Extras -->
          <div
            v-if="selectedApiDetails.expiration"
            class="mb-3"
          >
            <small class="text-muted">
              {{ $t('contaConfiguracao.exchangeApi.vencimento') }}: {{ selectedApiDetails.expiration ? formatDateBR(selectedApiDetails.expiration) : $t('contaConfiguracao.exchangeApi.naoExpira') }}
              <b-badge
                v-if="isExpiringSoon(selectedApiDetails.expiration)"
                variant="warning"
                class="ml-1"
              >
                {{ $t('contaConfiguracao.exchangeApi.proximoAVencer') }}
              </b-badge>
            </small>
          </div>

          <!-- Botões de Ação -->
          <div class="pt-2 border-top">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-1 mt-1"
              :disabled="loadingApiDetails"
              @click="refreshApiDetails"
            >
              <b-spinner
                v-if="loadingApiDetails"
                small
                class="mr-50"
              />
              <feather-icon
                v-else
                icon="RefreshCwIcon"
                size="14"
                class="mr-50"
              />
              {{ loadingApiDetails ? ($t('comum.carregando') || 'Carregando...') : ($t('contaConfiguracao.exchangeApi.atualizar') || 'Atualizar') }}
            </b-button>

            <b-button
              v-ripple.400="'rgba(234, 84, 85, 0.15)'
              "
              variant="outline-danger"
              class="mr-1 mt-1"
              @click="removeAPI(selectedCorretoraId)"
            >
              <feather-icon
                icon="Trash2Icon"
                size="14"
                class="mr-50"
              />
              {{ $t('contaConfiguracao.exchangeApi.excluir') || 'Excluir' }}
            </b-button>

            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              variant="outline-secondary"
              class="mt-1"
              @click="closeDetailsModal"
            >
              {{ $t('contaConfiguracao.exchangeApi.fechar') || 'Fechar' }}
            </b-button>
          </div>
        </div>

        <div
          v-else-if="!loadingApiDetails && !errorLoadingDetails && !(selectedApiDetails && selectedApiDetails.apiInvalid)"
          class="text-center py-4"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="32"
            class="text-warning mb-2"
          />
          <p>{{ $t('contaConfiguracao.exchangeApi.detalhesNaoEncontrados') || 'Não foi possível carregar os detalhes da API.' }}</p>
        </div>
      </b-overlay>
    </b-modal>
  </div>
</template>

<script>
import store from '@/store'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BButton,
  BForm,
  BFormGroup,
  BFormInput,
  BRow,
  BCol,
  BModal,
  BOverlay,
  BCard,
  BCardBody,
  BBadge,
  BInputGroup,
  BInputGroupAppend,
  BSpinner,
  VBTooltip,
  BAlert,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { formatDateBR } from '@/utils/filter'
import configuracaoContaStoreModule from './configuracaoContaStoreModule'

const CONFIGURACAO_CONTA_STORE_MODULE_NAME = 'configuracao-conta'

export default {
  components: {
    ValidationProvider,
    ValidationObserver,
    BButton,
    BForm,
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    BModal,
    BOverlay,
    BCard,
    BCardBody,
    BBadge,
    BInputGroup,
    BInputGroupAppend,
    BSpinner,
    BAlert,
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,

  },
  data() {
    return {
      // Lista hardcoded de corretoras
      corretoras: [
        { id: 1, nome: 'Binance' },
        { id: 2, nome: 'MEXC' },
      ],
      status: {},
      loading: false,
      loadingForm: false,
      showForm: false,
      showDetailsModal: false,
      formMode: 'create',
      selectedCorretoraId: null,
      selectedApiDetails: {},
      formData: {
        apiKey: '',
        apiSecret: '',
      },
      showSecretKey: false,
      loadingApiDetails: false,
      errorLoadingDetails: null,
      loadingPositionMode: false,
    }
  },
  created() {
    if (!store.hasModule(CONFIGURACAO_CONTA_STORE_MODULE_NAME)) {
      store.registerModule(CONFIGURACAO_CONTA_STORE_MODULE_NAME, configuracaoContaStoreModule)
    }
    this.loadAllStatus()
  },
  beforeDestroy() {
    if (store.hasModule(CONFIGURACAO_CONTA_STORE_MODULE_NAME)) {
      store.unregisterModule(CONFIGURACAO_CONTA_STORE_MODULE_NAME)
    }
  },
  methods: {

    isExpiringSoon(expirationDate) {
      if (!expirationDate) return false
      const expiry = new Date(expirationDate)
      const now = new Date()
      const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24))
      return daysUntilExpiry <= 7 && daysUntilExpiry >= 0 // 7 dias ou menos para expirar
    },

    async loadAllStatus() {
      this.loading = true
      try {
        // Uma única chamada para buscar todas as APIs configuradas
        const res = await this.$store.dispatch('configuracao-conta/getExchangeConfig')
        const configuredApis = res.data || []

        // Criar mapa de APIs configuradas por corretoraId
        const configuredApisMap = {}
        configuredApis.forEach(api => {
          configuredApisMap[api.corretoraId] = api
        })

        // Para cada corretora, verificar se tem API configurada
        const statusPromises = this.corretoras.map(async corretora => {
          const configuredApi = configuredApisMap[corretora.id]
          const status = {
            active: !!configuredApi,
            apiKey: configuredApi?.apiKey || '',
            expiration: configuredApi?.expiration || null,
            corretoraNome: corretora.nome,
            corretoraId: corretora.id,
            // Informações específicas da Binance
            spotEnabled: false,
            futuresEnabled: false,
            positionMode: null,
            apiInvalid: null,
            errorMessage: null,
          }

          // Para APIs configuradas, definir valores padrão sem chamar getApiInfo
          // getApiInfo será chamado apenas quando clicar em "Visualizar"
          if (configuredApi) {
            // Definir valores padrão para exibição na listagem
            status.spotEnabled = false
            status.futuresEnabled = false
            status.positionMode = null
            status.apiInvalid = null
            status.errorMessage = null
          }

          return { corretoraId: corretora.id, status }
        })

        const statusResults = await Promise.all(statusPromises)
        statusResults.forEach(({ corretoraId, status }) => {
          this.$set(this.status, corretoraId, status)
        })
      } catch (error) {
        // Em caso de erro, inicializar status vazio para todas as corretoras
        this.corretoras.forEach(corretora => {
          this.$set(this.status, corretora.id, {
            active: false,
            apiKey: '',
            expiration: null,
            corretoraNome: corretora.nome,
            corretoraId: corretora.id,
            spotEnabled: false,
            futuresEnabled: false,
            positionMode: null,
            apiInvalid: null,
            errorMessage: null,
          })
        })

        this.$swal({
          title: 'Erro!',
          text: 'Erro ao carregar configurações das APIs',
          icon: 'error',
          customClass: {
            confirmButton: 'btn btn-primary',
          },
          buttonsStyling: false,
        })
      } finally {
        this.loading = false
      }
    },
    getStatus(corretoraId) {
      return this.status[corretoraId] || {
        spotEnabled: false,
        futuresEnabled: false,
        positionMode: null,
        apiInvalid: false,
        errorMessage: null,
      }
    },
    cardBorderClass(corretoraId) {
      const currentStatus = this.getStatus(corretoraId)
      if (currentStatus.apiInvalid === true) return 'border-danger'
      if (currentStatus.apiInvalid === false) return 'border-success'
      return 'border-secondary'
    },
    maskKey(key) {
      if (!key) return ''
      return key.length > 8 ? `${key.slice(0, 4)}****${key.slice(-4)}` : '****'
    },

    openForm(corretoraId, mode = 'create') {
      this.selectedCorretoraId = corretoraId
      this.formMode = mode
      if (mode === 'edit') {
        this.formData.apiKey = this.getStatus(corretoraId).apiKey
        this.formData.apiSecret = ''
      } else {
        this.formData.apiKey = ''
        this.formData.apiSecret = ''
      }
      this.showForm = true
    },
    async validationForm() {
      const success = await this.$refs.simpleRules.validate()
      if (success) {
        this.loadingForm = true
        try {
          const payload = {
            apiKey: this.formData.apiKey,
            corretoraId: this.selectedCorretoraId,
          }

          // Só enviar apiSecret se não estiver em modo edit
          if (this.formMode !== 'edit') {
            payload.apiSecret = this.formData.apiSecret
          }

          await this.$store.dispatch('configuracao-conta/setExchangeConfig', payload)
          this.showForm = false
          await this.loadAllStatus()

          // Atualizar userData no localStorage com corretoras ativas
          await this.refreshUserData()

          this.$swal({
            title: 'Sucesso!',
            text: 'Configuração da API salva com sucesso',
            icon: 'success',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        } catch (error) {
          this.$swal({ title: 'Erro!', text: error.data, icon: 'error' })
        } finally {
          this.loadingForm = false
        }
      }
    },
    async removeAPI(corretoraId) {
      const result = await this.$swal({
        title: 'Tem certeza?',
        text: 'Você deseja remover esta configuração de API?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, remover!',
        cancelButtonText: 'Cancelar',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-secondary ms-1',
        },
        buttonsStyling: false,
      })

      if (result.isConfirmed) {
        try {
          await this.$store.dispatch('configuracao-conta/deleteExchangeConfig', { corretoraId })
          await this.loadAllStatus()

          // Atualizar userData no localStorage com corretoras ativas
          await this.refreshUserData()

          // Fechar o modal de detalhes se estiver aberto
          this.closeDetailsModal()

          this.$swal({
            title: 'Sucesso!',
            text: 'API removida com sucesso',
            icon: 'success',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        } catch (error) {
          this.$swal({
            title: 'Erro!',
            text: error.response?.data?.[0]?.erro || 'Erro ao remover API',
            icon: 'error',
            customClass: {
              confirmButton: 'btn btn-primary',
            },
            buttonsStyling: false,
          })
        }
      }
    },
    toggleSecretKeyVisibility() {
      this.showSecretKey = !this.showSecretKey
    },
    corretoraNome(id) {
      const found = this.corretoras.find(c => c.id === id)
      return found ? found.nome : ''
    },
    modalTitle() {
      if (!this.selectedCorretoraId) return ''
      if (this.formMode === 'edit') return `${this.$t('contaConfiguracao.exchangeApi.editar')} ${this.corretoraNome(this.selectedCorretoraId)}`
      return `${this.$t('contaConfiguracao.exchangeApi.cadastrar')} ${this.corretoraNome(this.selectedCorretoraId)}`
    },
    formatDateBR,

    async showApiDetails(corretoraId) {
      this.selectedCorretoraId = corretoraId
      this.selectedApiDetails = this.getStatus(corretoraId)
      this.showDetailsModal = true
      this.loadingApiDetails = true
      this.errorLoadingDetails = null

      try {
        const baseStatus = this.getStatus(corretoraId)
        if (!baseStatus.active) {
          this.errorLoadingDetails = 'API não encontrada'
          return
        }

        const updatedStatus = {
          ...baseStatus,
          active: true,
          corretoraId,
          corretoraNome: this.corretoraNome(corretoraId),
        }

        // Chamar getApiInfo para buscar informações detalhadas
        try {
          const apiInfoRes = await this.$store.dispatch('configuracao-conta/getApiInfo', {
            corretoraId,
          })

          if (apiInfoRes.data) {
            // Normalizar chaves vindas do backend (pt/en)
            const apiInvalid = (apiInfoRes.data.apiInvalid !== undefined)
              ? apiInfoRes.data.apiInvalid
              : apiInfoRes.data.apiInvalida
            const errorMessage = apiInfoRes.data.errorMessage || apiInfoRes.data.mensagemErro

            // Verificar se a API está inválida
            if (apiInvalid) {
              updatedStatus.apiInvalid = true
              updatedStatus.errorMessage = errorMessage
              updatedStatus.spotEnabled = false
              updatedStatus.futuresEnabled = false
              updatedStatus.positionMode = null
            } else {
              // API válida - processar informações normalmente
              updatedStatus.apiInvalid = false
              updatedStatus.errorMessage = null
              updatedStatus.spotEnabled = apiInfoRes.data.enableSpotAndMarginTrading || false
              updatedStatus.futuresEnabled = apiInfoRes.data.enableFutures || false

              // Se futures estiver habilitado e for Binance, buscar modo de posição atualizado
              if (updatedStatus.futuresEnabled && this.selectedCorretoraId === 1) {
                try {
                  const hedgeRes = await this.$store.dispatch('configuracao-conta/getModoPosicaoHedge')
                  // API may return boolean (true = hedge) or string ('hedge' | 'single')
                  const apiValue = hedgeRes && hedgeRes.data
                  updatedStatus.positionMode = (apiValue === true || apiValue === 'hedge') ? 'hedge' : 'single'
                } catch (hedgeError) {
                  updatedStatus.positionMode = 'single'
                }
              }
            }
          }
        } catch (apiError) {
          // Manter valores padrão se der erro
          updatedStatus.spotEnabled = false
          updatedStatus.futuresEnabled = false
          updatedStatus.positionMode = null
        }

        // Atualizar os dados exibidos
        this.selectedApiDetails = updatedStatus

        // Atualizar também o status global
        this.$set(this.status, corretoraId, updatedStatus)
      } catch (error) {
        this.errorLoadingDetails = error.response?.data?.[0]?.erro || 'Erro ao carregar detalhes da API'
      } finally {
        this.loadingApiDetails = false
      }
    },

    async refreshApiDetails() {
      if (!this.selectedCorretoraId) return

      try {
        this.loadingApiDetails = true
        this.errorLoadingDetails = null

        const baseStatus = this.getStatus(this.selectedCorretoraId)
        if (!baseStatus.active) {
          this.errorLoadingDetails = 'API não encontrada'
          return
        }

        const updatedStatus = {
          ...baseStatus,
          createdAt: baseStatus.createdAt || this.selectedApiDetails.createdAt,
        }

        // Buscar informações específicas atualizadas
        try {
          const apiInfoRes = await this.$store.dispatch('configuracao-conta/getApiInfo', {
            corretoraId: this.selectedCorretoraId,
          })

          if (apiInfoRes.data) {
            // Normalizar chaves vindas do backend (pt/en)
            const apiInvalid = (apiInfoRes.data.apiInvalid !== undefined)
              ? apiInfoRes.data.apiInvalid
              : apiInfoRes.data.apiInvalida
            const errorMessage = apiInfoRes.data.errorMessage || apiInfoRes.data.mensagemErro

            // Verificar se a API está inválida
            if (apiInvalid) {
              updatedStatus.apiInvalid = true
              updatedStatus.errorMessage = errorMessage
              updatedStatus.spotEnabled = false
              updatedStatus.futuresEnabled = false
              updatedStatus.positionMode = null
            } else {
              // API válida - processar informações normalmente
              updatedStatus.apiInvalid = false
              updatedStatus.errorMessage = null
              updatedStatus.spotEnabled = apiInfoRes.data.enableSpotAndMarginTrading || false
              updatedStatus.futuresEnabled = apiInfoRes.data.enableFutures || false

              // Se futures estiver habilitado e for Binance, buscar modo de posição atualizado
              if (updatedStatus.futuresEnabled && this.selectedCorretoraId === 1) {
                try {
                  const hedgeRes = await this.$store.dispatch('configuracao-conta/getModoPosicaoHedge')
                  // API may return boolean (true = hedge) or string ('hedge' | 'single')
                  const apiValue = hedgeRes && hedgeRes.data
                  updatedStatus.positionMode = (apiValue === true || apiValue === 'hedge') ? 'hedge' : 'single'
                } catch (hedgeError) {
                  updatedStatus.positionMode = 'single'
                }
              }
            }
          }
        } catch (apiError) {
          this.$toast.error('Erro ao atualizar informações')
        }

        // Atualizar os dados exibidos
        this.selectedApiDetails = updatedStatus

        // Atualizar também o status global
        this.$set(this.status, this.selectedCorretoraId, updatedStatus)

        this.$toast.success('Informações atualizadas com sucesso!')
      } catch (error) {
        this.errorLoadingDetails = error.response?.data?.[0]?.erro || 'Erro ao carregar detalhes da API'
        this.$toast.error('Erro ao atualizar informações')
      } finally {
        this.loadingApiDetails = false
      }
    },

    /**
     * Fecha o modal de detalhes e limpa os estados
     */
    closeDetailsModal() {
      this.showDetailsModal = false
      // Usamos setTimeout para garantir que a animação do modal termine antes de limpar os dados
      setTimeout(() => {
        this.selectedApiDetails = {}
        this.errorLoadingDetails = null
        this.loadingApiDetails = false
      }, 300)
    },

    /**
     * Fecha os detalhes da API e limpa os estados
     */
    closeApiDetails() {
      this.selectedApiDetails = {}
      this.errorLoadingDetails = null
      this.loadingApiDetails = false
    },

    async refreshUserData() {
      try {
        // Determinar corretoras ativas a partir do estado local
        const corretorasAtivas = Object.keys(this.status)
          .map(id => ({ id: Number(id), s: this.status[id] }))
          .filter(({ s }) => s && s.active)
          .map(({ id }) => {
            if (id === 0) return 'BINANCE'
            if (id === 1) return 'MEXC'
            return null
          })
          .filter(Boolean)

        // Atualizar userData no localStorage
        const currentUserData = JSON.parse(localStorage.getItem('userData') || '{}')
        currentUserData.corretorasAtivas = corretorasAtivas
        localStorage.setItem('userData', JSON.stringify(currentUserData))

        // Emitir evento para notificar outros componentes
        this.$root.$emit('userDataUpdated')
      } catch (error) {
        this.$toast.error('Erro ao atualizar informações')
      }
    },

    async togglePositionMode() {
      if (!this.selectedCorretoraId) return

      try {
        this.loadingPositionMode = true
        await this.$store.dispatch('configuracao-conta/setModoPosicaoHedge', String(this.selectedApiDetails.positionMode !== 'hedge'))

        // Como o backend retorna void, atualizamos a flag baseado no valor enviado
        const newMode = this.selectedApiDetails.positionMode !== 'hedge'
        this.selectedApiDetails.positionMode = newMode ? 'hedge' : 'single'
        this.$set(this.status, this.selectedCorretoraId, this.selectedApiDetails)
        this.$toast.success('Modo de posição alternado com sucesso!')
      } catch (error) {
        this.$toast.error('Erro ao alternar modo de posição')
      } finally {
        this.loadingPositionMode = false
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-sweetalert.scss';
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

.avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;

  &.avatar-sm {
    width: 32px;
    height: 32px;
  }
}

.bg-light-primary {
  background-color: rgba(59, 130, 246, 0.12) !important;
  color: #3b82f6;
}

.border-success {
  border-color: #28c76f !important;
  border-width: 2px !important;
}

.border-danger {
  border-color: #ea5455 !important;
  border-width: 2px !important;
}

.border-secondary {
  border-color: #6c757d !important;
  border-width: 2px !important;
}

.dark-layout {
  div ::v-deep .card {
    .b-overlay {
      .bg-light {
        background-color: $theme-dark-body-bg !important;
      }
    }
  }
}
</style>
